import {BaseMessenger} from 'src/messengers/base';
import {INCREMENT_EVENTS} from 'src/metrics';

import type {UnsubscribeFn} from 'src/messengers/types';
import type {Message} from 'src/target/context';

/**
 * UnmanagedMessenger is a messenger that provides the basic secure messaging functionality.
 * It does not provide any additional data verification or schema enforcement.
 *
 * It is only meant to be used when communicating with non-Stripe (uncontrolled) contexts.
 * For communication between controlled contexts, use ManagedMessenger or RPCMessenger instead.
 *
 * Learn more at https://go/sail/comms/guides/unmanaged-messenger
 */
export class UnmanagedMessenger extends BaseMessenger {
  protected messengerType = 'unmanaged';

  /**
   * Send a message to the remote target
   */
  sendMessage<T = unknown>(data: T, transfer?: Transferable[]): void {
    this.debug('Sending message');
    this.sendRawMessage(data, transfer);
  }

  /**
   * Listen for messages from the remote target. The callback will be called
   * every time a message is received. To stop listening, call the returned unsubscribe function.
   */
  onMessage<T = unknown>(callback: (data: T) => void): UnsubscribeFn {
    this.debug('👂 listening..');
    const wrappedListener = (message: Message<T>): void => {
      this.debug('📥 received message');
      this.increment(INCREMENT_EVENTS.ON_MESSAGE_RECEIVED);
      callback(message.data);
    };

    this.addMessageEventListener<T>(wrappedListener);

    return () => {
      this.debug('🙉 stopped listening');
      this.removeMessageEventListener(wrappedListener);
    };
  }

  /**
   * Listen for a single message from the remote target and unsubscribe automatically after receiving it.
   * The callback will be called when a valid message is received.
   * You can also stop listening anytime by calling the returned unsubscribe function.
   */
  onceMessage<T = unknown>(
    callback: (data: T) => void,
    opts?: {isValid: (data: T) => boolean},
  ): UnsubscribeFn {
    this.debug('👂 listening for a single message');
    const wrappedListener = (message: Message<T>): void => {
      const isValid = opts?.isValid ?? ((): boolean => true);
      if (isValid(message.data)) {
        this.debug('📥 received once message');
        this.increment(INCREMENT_EVENTS.ONCE_MESSAGE_RECEIVED);
        callback(message.data);
        this.removeMessageEventListener(wrappedListener);
      }
    };

    this.addMessageEventListener<T>(wrappedListener);

    return () => {
      this.debug('🙉 stopped listening for once message');
      this.removeMessageEventListener(wrappedListener);
    };
  }
}
